import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, Save, KeyRound, ShieldCheck, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { platformApi } from "../../lib/api";

const inp =
  "w-full h-9 px-2.5 rounded-md border border-white/10 bg-[#0F1623] text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/50 transition-colors";

/** Platform-wide Resend config (super-admin). Sends invites, demo notices, resets. */
export default function PlatformEmailCard() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState(""); // write-only; only sent when non-empty
  const [webhookSecret, setWebhookSecret] = useState(""); // write-only; only sent when non-empty
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reload = async () => {
    try {
      const cfg = await platformApi.email.getSettings();
      setS(cfg);
      setDirty(false);
      setApiKey("");
      setWebhookSecret("");
    } catch {
      toast.error("Failed to load platform email settings");
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const mark = (patch) => {
    setS((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (s.fromEmail && !s.fromEmail.includes("@")) {
      toast.error("Enter a valid From email address.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fromName: s.fromName || "",
        fromEmail: s.fromEmail || "",
        replyTo: s.replyTo || "",
        inboundEmail: s.inboundEmail || "",
        ...(apiKey ? { resendApiKey: apiKey } : {}),
        ...(webhookSecret ? { resendWebhookSecret: webhookSecret } : {}),
      };
      const updated = await platformApi.email.updateSettings(payload);
      setS(updated);
      setApiKey("");
      setWebhookSecret("");
      setDirty(false);
      toast.success("Platform email settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error("Enter an email address to test.");
      return;
    }
    if (dirty) {
      toast.error("Save your settings before sending a test.");
      return;
    }
    setTesting(true);
    try {
      await platformApi.email.sendTest(testTo.trim());
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test email failed");
    } finally {
      setTesting(false);
    }
  };

  if (!s) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400" data-testid="platform-email-card">
        <Loader2 className="h-4 w-4 inline animate-spin" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5 max-w-2xl" data-testid="platform-email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4 text-cyan-400" />
        <h2 className="text-[14px] font-semibold text-slate-100">Platform email (Resend)</h2>
        <span
          data-testid="platform-email-status"
          className={`ml-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            s.configured
              ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
              : "text-amber-300 bg-amber-500/10 border-amber-500/30"
          }`}
        >
          {s.configured ? (s.source === "db" ? "Resend connected" : "Using env key") : "Not configured"}
        </span>
      </div>
      <p className="text-[12px] text-slate-400 mb-4">
        Sends platform emails — tenant &amp; staff invites, demo notifications, password resets — via Resend. The API key is
        stored encrypted and never shown again.{" "}
        <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline inline-flex items-center gap-0.5">
          Get a key <ExternalLink className="h-3 w-3" />
        </a>
      </p>

      <div className="space-y-3">
        <Field label="Resend API key">
          <div className="relative">
            <KeyRound className="h-3.5 w-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setDirty(true);
              }}
              data-testid="platform-email-key"
              placeholder={s.resendApiKeyConfigured ? "•••••••• (saved — leave blank to keep)" : "re_..."}
              className={`${inp} pl-8`}
            />
          </div>
        </Field>
        <Field label="Resend webhook secret">
          <div className="relative">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => {
                setWebhookSecret(e.target.value);
                setDirty(true);
              }}
              data-testid="platform-email-webhook-secret"
              placeholder={s.resendWebhookSecretConfigured ? "•••••••• (saved — leave blank to keep)" : "whsec_..."}
              className={`${inp} pl-8`}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            From the Resend Webhooks page — used to verify inbound platform webhook signatures.
          </p>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="From name">
            <input value={s.fromName || ""} onChange={(e) => mark({ fromName: e.target.value })} data-testid="platform-email-from-name" placeholder="ServiceGuru Pro" className={inp} />
          </Field>
          <Field label="From email">
            <input value={s.fromEmail || ""} onChange={(e) => mark({ fromEmail: e.target.value })} data-testid="platform-email-from-email" placeholder="info@yourdomain.com" className={inp} />
          </Field>
        </div>
        <Field label="Reply-to (optional)">
          <input value={s.inboundEmail || s.replyTo || ""} disabled={!!s.inboundEmail} onChange={(e) => mark({ replyTo: e.target.value })} data-testid="platform-email-reply-to" placeholder="support@yourdomain.com" className={`${inp} disabled:opacity-60`} />
        </Field>
        <Field label="Unique inbound email address">
          <input type="email" value={s.inboundEmail || ""} onChange={(e) => mark({ inboundEmail: e.target.value })} data-testid="platform-email-inbound-address" placeholder="Your configured platform receiving address" className={inp} />
        </Field>
        <p className="text-[11px] text-slate-500">
          The From address must be on a domain verified in your Resend account (or use <span className="text-slate-300">onboarding@resend.dev</span> for testing).
        </p>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0F1623] p-3.5 mt-4">
        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Send a test email</label>
        <div className="flex items-end gap-2">
          <div className="relative flex-1">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="platform-email-test-to" placeholder="you@example.com" className={`${inp} pl-8`} />
          </div>
          <Button onClick={sendTest} disabled={testing || !s.configured} data-testid="platform-email-test-btn" className="h-9 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-100 border border-cyan-500/30 text-[13px] font-semibold">
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send test
          </Button>
        </div>
        {dirty && <p className="text-[11px] text-amber-400 mt-2">Save your changes before sending a test.</p>}
      </div>

      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={!dirty || saving} data-testid="platform-email-save-btn" className="h-10 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[13px] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
